import React from 'react';
import { Link } from 'react-router-dom';
import StudyStayLogo from './StudyStayLogo';
import { useAuth } from '../contexts/AuthContext';
import { 
  Home, 
  Star, 
  MessageCircle, 
  User, 
  FileText, 
  Calendar, 
  CalendarDays,
  Building,
  Mail,
  Phone,
  MapPin
} from 'lucide-react';

export default function WebsiteFooter() {
  const { userType } = useAuth();
  
  return (
    <footer className="mt-auto text-slate-700 overflow-hidden relative border-t border-slate-200/80 bg-gradient-to-b from-white via-[#FBFDFF] to-[#F6FAFF]">
      {/* Soft background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 right-0 w-72 h-72 bg-[#5EABEB]/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-28 left-0 w-72 h-72 bg-[#5EABEB]/7 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container-professional relative z-10">
        <div className="py-12 md:py-14">
          <div className={`grid grid-cols-1 md:grid-cols-2 ${userType === 'landlord' || !userType ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-8`}>
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="bg-[#EAF4FE] rounded-xl p-2.5 border border-[#5EABEB]/20">
                  <StudyStayLogo className="w-7 h-7 text-[#5EABEB]" />
                </div>
                <span className="text-xl font-semibold text-slate-900">StudyStay</span>
              </div>
              <div className="text-slate-600 text-sm leading-relaxed space-y-1">
                <p><strong>Nolf Technologies OÜ</strong></p>
                <p>Private limited company (OÜ)</p>
              </div>
            </div>

            {/* For Students - Only show if user is tenant or not logged in */}
            {(userType === 'tenant' || !userType) && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Voor Studenten</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Zoek Kamers
                    </Link>
                  </li>
                  <li>
                    <Link to="/favorites" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Mijn Favorieten
                    </Link>
                  </li>
                  <li>
                    <Link to="/messages" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Berichten
                    </Link>
                  </li>
                  <li>
                    <Link to="/account" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Mijn Account
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            {/* For Landlords - Only show if user is landlord */}
            {userType === 'landlord' && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-900">Voor Verhuurders</h3>
                <ul className="space-y-3">
                  <li>
                    <Link to="/landlord" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link to="/landlord/contracts" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Contracten
                    </Link>
                  </li>
                  <li>
                    <Link to="/landlord/viewings" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Bezichtigingen
                    </Link>
                  </li>
                  <li>
                    <Link to="/landlord/agenda" className="text-slate-600 hover:text-[#2B6FA6] transition-all duration-200 text-sm flex items-center group">
                      <span className="w-1.5 h-1.5 bg-slate-300 rounded-full mr-2 group-hover:bg-[#5EABEB] transition-all duration-200"></span>
                      Agenda
                    </Link>
                  </li>
                </ul>
              </div>
            )}

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">Contact</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 group">
                  <div className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center group-hover:border-[#5EABEB]/35 transition-all duration-200">
                    <MapPin className="w-4 h-4 text-slate-500" />
                  </div>
                  <span className="text-slate-600 text-sm group-hover:text-slate-800 transition-colors duration-200">Tornimäe tn 5, 10145 Tallinn, Estonia</span>
                </div>
                <div className="flex items-center space-x-3 group">
                  <div className="w-8 h-8 bg-white border border-slate-200 rounded-lg flex items-center justify-center group-hover:border-[#5EABEB]/35 transition-all duration-200">
                    <Mail className="w-4 h-4 text-slate-500" />
                  </div>
                  <Link to="/contact" className="text-slate-600 text-sm group-hover:text-[#2B6FA6] transition-colors duration-200 hover:underline">info@studystay.org</Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-200/80 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Nolf Technologies OÜ. Alle rechten voorbehouden.
            </div>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/legal" className="text-slate-500 hover:text-[#2B6FA6] transition-all duration-200 text-sm font-medium hover:underline">
                Alle Juridische Documenten
              </Link>
              <Link to="/legal/privacy-policy" className="text-slate-500 hover:text-[#2B6FA6] transition-all duration-200 text-sm font-medium hover:underline">
                Privacy Policy
              </Link>
              <Link to="/legal/terms-of-service" className="text-slate-500 hover:text-[#2B6FA6] transition-all duration-200 text-sm font-medium hover:underline">
                Terms of Service
              </Link>
              <Link to="/legal/cookie-policy" className="text-slate-500 hover:text-[#2B6FA6] transition-all duration-200 text-sm font-medium hover:underline">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
